import React, { SyntheticEvent, useEffect, useMemo, useState } from "react";

import {
  ButtonProps,
  CheckboxProps,
  Dropdown,
  DropdownProps,
  Form,
  Modal,
  Radio,
  RadioProps,
  TextArea,
  TextAreaProps,
} from "semantic-ui-react";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";

import ModConfirm from "react-lib/apps/common/cnmi/ModConfirm";
import ModalEditImage from "react-lib/apps/common/ModalEditImage";
import ModInfo from "react-lib/apps/common/ModInfo";
import SnackMessage from "react-lib/apps/common/SnackMessage";

import CardRehabTreatmentUX from "./CardRehabTreatmentUX";
import CardSelectAppointmentDatetime from "./CardSelectAppointmentDatetime";
import TableTreatment, { ColumnKeys } from "./TableTreatment";

import {
  CheckedType,
  formatRehabImage,
  LANGUAGE_OPTIONS,
  LanguageType,
  RehabOrderType,
  RehabPlanType,
} from "./RHBInterface";

import { getOrdinal } from "react-lib/utils/dateUtils";

import "./CardRehabTreatment.scss"

type CardRehabTreatmentPlanProps = {
  onEvent: (e: any) => any;
  setProp: (key: string, value: any, callback?: () => void) => any;
  proxyController?: any;
  checkedList?: CheckedType[];
  closeIcon?: boolean;
  divisionId?: any;
  hideColumns?: ColumnKeys[];
  hideEditDepartment?: boolean;
  hideFilter?: boolean;
  hideMode?: boolean;
  isIncludePerformed: boolean;
  showOnlyCancelledPlan: boolean;
  rehabDeviceSlot?: any;
  rehabOrderAppointmentList?: any;
  rehabTreatmentList?: RehabPlanType[];
  rehabTreatmentPerformList?: RehabOrderType[];
  selectable?: boolean;
  selectedRehabDeviceSlot?: any;
  buttonLoadCheck?: any;
  errorMessage: any;
  languageUX: "en" | "th";
  masterOptions?: Record<string, Record<string, any>[] | undefined>;
  searchedItemListWithKey: any;
  selectedPatient: any;
  onChangeIncludePerformed?: (e: SyntheticEvent, data: CheckboxProps) => void;
  onChangeShowOnlyCancelledplan?: (e: SyntheticEvent, data: CheckboxProps) => void;
  onChangeMenu?: (menu: string) => void;
  onClose?: () => void;
  onSelectRehab?: (data: Record<string, any>) => void;
};

type ModConfirmType = {
  id: number;
  hasApp?: boolean;
  note: string;
  type?: string;
};

const BUTTON_ACTIONS = {
  cancel: "CANCEL",
  confirm_perform: "CONFIRM_PERFORM",
  edit: "EDIT",
  perform: "PERFORM",
  print: "PRINT",
};

const CARD_REHAB_TREATMENT = "CardRehabTreatment";

const ACTION_PERFORM = `${CARD_REHAB_TREATMENT}_${BUTTON_ACTIONS.perform}`;
const ACTION_CONFIRM_PERFORM = `${CARD_REHAB_TREATMENT}_${BUTTON_ACTIONS.confirm_perform}`;
const ACTION_CANCEL = `${CARD_REHAB_TREATMENT}_${BUTTON_ACTIONS.cancel}`;
const ACTION_EDIT = `${CARD_REHAB_TREATMENT}_${BUTTON_ACTIONS.edit}`;
const ACTION_PRINT = `${CARD_REHAB_TREATMENT}_${BUTTON_ACTIONS.print}`;

const CardRehabTreatment = (props: CardRehabTreatmentPlanProps) => {
  const [selectedMode, setSelectedMode] = useState<"" | "APPOINTMENT" | "PERFORM" | "PRINT">("");
  const [activePanes, setActivePanes] = useState<Record<string, any>[]>([]);
  const [imageOverlay, setImageOverlay] = useState<Record<string, any>[]>([]);

  const [openModalEditImage, setOpenModalEditImage] = useState<boolean>(false);
  const [openModErrorRepetition, setOpenModErrorRepetition] = useState<boolean>(false);
  const [openModSelectAppointment, setOpenModSelectAppointment] = useState<boolean>(false);

  const [modConfirmCancel, setModConfirmCancel] = useState<ModConfirmType | null>(null);
  const [modConfirmPerform, setModConfirmPerform] = useState<{ id?: number; type?: string } | null>(
    null
  );
  const [modEditDepartment, setModEditDepartment] = useState<{
    id: number;
    divisions: number[];
    to_department_for_physiotherapist?: number;
  } | null>(null);

  const [checkedList, setCheckedList] = useState<CheckedType[]>([]);
  const [language, setLanguage] = useState<LanguageType>("TH");

  console.log("CardRehabTreatment props:", props);

  useEffect(() => {
    props.onEvent({
      message: "GetMasterData",
      params: {
        masters: [
          ["rhb_precaution", {}],
          ["RehabilitationList", {}],
          ["prenameTh", {}],
          ["prenameEn", {}],
        ],
      },
    });
  }, []);

  useEffect(() => {
    if (props.selectedPatient && props.selectable) {
      setSelectedMode("APPOINTMENT");
    }
  }, [props.selectedPatient?.id]);

  useEffect(() => {
    setCheckedList(props.checkedList ?? []);
  }, [props.checkedList]);

  useEffect(() => {
    setLanguage(props.languageUX.toUpperCase() as LanguageType);
  }, [props.languageUX]);

  const treatmentPending = useMemo(() => {
    const filtered = (props.rehabTreatmentPerformList ?? []).filter((order) => {
      const flatItems = order.items.flatMap((item) => item.plan_item_detail?.performed ?? []);

      return !flatItems.some((item) => item.rehab_order_id === order.id);
    });

    return filtered
      .map((order) => ({
        ...order,
        items: order.items,
      }))
      .filter((order) => order.items.length > 0);
  }, [props.rehabTreatmentPerformList]);

  const toDepartmentOptions = useMemo(() => {
    const options = props.masterOptions?.division ?? [];

    if (!modEditDepartment?.divisions.length) {
      return options;
    }

    return options.filter((option) => modEditDepartment.divisions.includes(option.value));
  }, [modEditDepartment, props.masterOptions]);

  const handleSelectMode = (e: SyntheticEvent, data: ButtonProps) => {
    setSelectedMode(data.name);
    setCheckedList([]);
  };

  const handleCancelMode = () => {
    setSelectedMode("");
    setCheckedList([]);
  };

  const handleOpenGallery = (images: any[]) => {
    const formattedImage = formatRehabImage(images);

    setImageOverlay(formattedImage.imageOverlay);
    setActivePanes(formattedImage.activePanes);
    setOpenModalEditImage(true);
  };

  const handleOpenHistory = (data: Record<string, any>) => {
    props.setProp("goToMenu", "Rehabilitation History");
    props.setProp("viewRehabHistoryId", data.rehab_order_id);
  };

  const handleCloseEditImage = () => {
    setImageOverlay([]);
    setActivePanes([]);
    setOpenModalEditImage(false);
  };

  const handleChangeRepetition = (
    value: string,
    params: { btnAction: string; data: Record<string, any> }
  ) => {
    const number = Number(value);
    const { btnAction, data } = params;

    // กรณีจำนวนใหม่ < performed_count หรือตรงกับ total เดิม → ไม่ต้องทำอะไร
    if (number < data.performed_count || data.total_for_physiotherapist === number) {
      return;
    }

    const appointments: Record<string, any>[] = data.appointments;

    // นับจำนวน appointments ที่มีวันที่นัด
    const appointmentCount = appointments.filter((item) => item.appointment_date).length;

    // ถ้าจำนวนใหม่ < (performed_count + นัดที่มีอยู่) → แสดง modal แจ้ง error
    if (number < data.performed_count + appointmentCount) {
      setOpenModErrorRepetition(true);

      return;
    }

    props.onEvent({
      message: "updateRhbExamPlanItemTotal",
      params: {
        btnAction,
        card: CARD_REHAB_TREATMENT,
        data: { ...data, total_for_physiotherapist: number },
      },
    });
  };

  const handleCloseModInfo = () => {
    setOpenModErrorRepetition(false);
  };

  const handleOpenModConfirmCancel = (data: Record<string, any>) => {
    const appointments: Record<string, any>[] = data.appointments;
    const hasApp = appointments.some((app) => app.appointment_date);

    setModConfirmCancel({
      id: data.id,
      hasApp,
      note: "",
      type: hasApp ? "WITHOUT_APPOINTMENT" : "",
    });
  };

  const handleCloseConfirmCancel = () => {
    setModConfirmCancel(null);
  };

  const handleChangeNote = (e: SyntheticEvent, data: TextAreaProps) => {
    if (modConfirmCancel) {
      setModConfirmCancel({ ...modConfirmCancel, note: String(data.value) });
    }
  };

  const handleChangeType = (e: SyntheticEvent, data: RadioProps) => {
    if (modConfirmCancel) {
      setModConfirmCancel({ ...modConfirmCancel, type: data.name });
    }
  };

  const handleChangeCheckedPerform = (data: Record<string, any>) => {
    const { checked, ...item } = data;

    if (checked) {
      setCheckedList([...checkedList, item as any]);
    } else {
      const filtered = checkedList.filter(
        (perform) => perform.plan_item !== item.plan_item || item.no !== perform.no
      );

      setCheckedList(filtered);
    }
  };

  const handlePerformTreatment = (
    data: { action?: string; id?: number; btnAction?: string; confirm?: boolean } = {}
  ) => {
    if (treatmentPending.length > 0 && !data.confirm) {
      setModConfirmPerform({ type: "CREATE_NEW" });

      return;
    }

    props.onEvent({
      message: "updateRhbTreatmentOrder",
      params: {
        action: data.action || "REQUEST",
        id: data.id,
        btnAction: data.btnAction || ACTION_PERFORM,
        card: CARD_REHAB_TREATMENT,
        items: checkedList,
        onSuccess: () => {
          handleSuccess();

          props.onChangeMenu?.("treatmentPerformRecord");
        },
      },
    });
  };

  const handleSuccess = () => {
    handleCancelMode();
    handleCloseModConfirmPerform();
    handleCloseModSelectAppointment();
  };

  const handleCloseModConfirmPerform = () => {
    setModConfirmPerform(null);
  };

  const handleConfirmPerform = () => {
    if (modConfirmPerform) {
      if (modConfirmPerform.type === "CREATE_NEW") {
        handlePerformTreatment({ btnAction: ACTION_CONFIRM_PERFORM, confirm: true });
      } else if (modConfirmPerform.type === "ADD_ITEM") {
        handlePerformTreatment({
          action: "ADD_ITEM",
          id: modConfirmPerform.id,
          btnAction: ACTION_CONFIRM_PERFORM,
          confirm: true,
        });
      }
    }
  };

  const handleMakeAppointment = () => {
    props.setProp("selectedRehabDeviceSlot", null);

    setOpenModSelectAppointment(true);
  };

  const handleCloseModSelectAppointment = () => {
    setOpenModSelectAppointment(false);
  };

  const handleSelectRehab = () => {
    props.onSelectRehab?.(checkedList);
  };

  const handleConfirmCancel = () => {
    if (!modConfirmCancel) {
      return;
    }

    props.onEvent({
      message: "deleteRhbPlanItem",
      params: {
        id: modConfirmCancel.id,
        btnAction: ACTION_CANCEL,
        cancel_all: modConfirmCancel.type === "WITH_APPOINTMENT",
        canceled_reason: modConfirmCancel.note,
        card: CARD_REHAB_TREATMENT,
        onSuccess: handleCloseConfirmCancel,
      },
    });
  };

  const handleOpenModEditDepartment = (data: Record<string, any>) => {
    setModEditDepartment({
      id: data.id,
      divisions: data.divisions,
      to_department_for_physiotherapist: data.to_department_for_physiotherapist,
    });
  };

  const handleCloseModEditDepartment = () => {
    setModEditDepartment(null);
  };

  const handleConfirmEdit = () => {
    if (!modEditDepartment) {
      return;
    }

    props.onEvent({
      message: "updateRhbExamPlanItem",
      params: {
        btnAction: ACTION_EDIT,
        card: CARD_REHAB_TREATMENT,
        data: { ...modEditDepartment },
        onSuccess: handleCloseModEditDepartment,
      },
    });
  };

  const handleChangeToDepartment = (e: SyntheticEvent, data: DropdownProps) => {
    if (modEditDepartment) {
      setModEditDepartment({
        ...modEditDepartment,
        to_department_for_physiotherapist: Number(data.value),
      });
    }
  };

  const handleChangeIsContinue = (checked: boolean, data: Record<string, any>) => {
    props.onEvent({
      message: "updateRhbExamPlanItem",
      params: {
        btnAction: data.btnAction,
        card: CARD_REHAB_TREATMENT,
        data: { id: data.id, is_continue: checked },
      },
    });
  };

  const handleChangeLanguage = (e: SyntheticEvent, data: DropdownProps) => {
    setLanguage(data.value as LanguageType);
  };

  const handlePrintBooking = () => {
    props.onEvent({
      message: "printRhbBookingForm",
      params: {
        btnAction: ACTION_PRINT,
        card: CARD_REHAB_TREATMENT,
        items: checkedList,
        language,
      },
    });
  };

  return (
    <div style={{ margin: "0 5px" }}>
      <SnackMessage
        error={props.errorMessage?.[CARD_REHAB_TREATMENT]}
        success={null}
        onClose={() => {
          props.setProp(`errorMessage.${CARD_REHAB_TREATMENT}`, null);
        }}
      />

      <CardRehabTreatmentUX
        closeIcon={props.closeIcon}
        disabledAppointment={checkedList.length === 0}
        hideFilter={props.hideFilter}
        hideMode={props.hideMode}
        isIncludePerformed={props.isIncludePerformed}
        showOnlyCancelledPlan={props.showOnlyCancelledPlan}
        language={language}
        selectedMode={selectedMode}
        languageUX={props.languageUX}
        languageOptions={LANGUAGE_OPTIONS}
        onCancelMode={handleCancelMode}
        onChangeIncludePerformed={props.onChangeIncludePerformed}
        onChangeShowOnlyCancelledplan={props.onChangeShowOnlyCancelledplan}
        onChangeLanguage={handleChangeLanguage}
        onClose={props.onClose}
        onMakeAppointment={handleMakeAppointment}
        onSelectMode={handleSelectMode}
        onSelectRehab={props.onSelectRehab ? handleSelectRehab : null}
        buttonPerform={
          <ButtonLoadCheck
            setProp={props.setProp}
            color="green"
            disabled={checkedList.length === 0}
            icon="check square outline"
            paramKey={ACTION_PERFORM}
            size="mini"
            title="Select to perform"
            buttonLoadCheck={props.buttonLoadCheck?.[ACTION_PERFORM]}
            onClick={() => {
              handlePerformTreatment();
            }}
          />
        }
        buttonPrint={
          <ButtonLoadCheck
            setProp={props.setProp}
            color="yellow"
            disabled={checkedList.length === 0}
            icon="print"
            paramKey={ACTION_PRINT}
            size="mini"
            title="Print Booking"
            buttonLoadCheck={props.buttonLoadCheck?.[ACTION_PRINT]}
            onClick={() => {
              handlePrintBooking();
            }}
          />
        }
        treatmentPlan={
          <TableTreatment
            checkedPerformList={checkedList}
            data={props.rehabTreatmentList}
            hideColumns={props.hideColumns}
            hideEditDepartment={props.hideEditDepartment}
            isIncludePerformed={props.isIncludePerformed}
            showOnlyCancelledPlan={props.showOnlyCancelledPlan}
            selectedMode={selectedMode}
            buttonLoadCheck={props.buttonLoadCheck}
            onCancelPlan={handleOpenModConfirmCancel}
            onChangeCheckPerform={handleChangeCheckedPerform}
            onChangeIsContinue={handleChangeIsContinue}
            onChangeRepetition={handleChangeRepetition}
            onEditDepartment={handleOpenModEditDepartment}
            onOpenGallery={handleOpenGallery}
            onOpenHistory={handleOpenHistory}
          />
        }
      />

      <ModalEditImage
        activePanes={activePanes}
        cancelImage={handleCloseEditImage}
        hiddenTab={false}
        hideBabyTooth={false}
        imageOverlay={imageOverlay}
        openEditModal={openModalEditImage}
        tabAddControl={null}
        readOnly
        onCloseModal={handleCloseEditImage}
      />

      <ModInfo
        open={openModErrorRepetition}
        titleColor="red"
        onApprove={handleCloseModInfo}
        onClose={handleCloseModInfo}
      >
        <div style={{ fontWeight: "bold", padding: "1rem 0" }}>
          Cannot modify the total quantity as it affects the appointment schedule
        </div>
      </ModInfo>

      <ModInfo
        style={{ width: "36rem" }}
        btnLoading={!!props.buttonLoadCheck?.[ACTION_CONFIRM_PERFORM]}
        buttonColor="green"
        open={!!modConfirmPerform}
        titleColor="blue"
        onApprove={handleConfirmPerform}
        onClose={handleCloseModConfirmPerform}
      >
        <div>
          <div style={{ fontWeight: "bold", padding: "1rem 0 1.5rem" }}>
            Would you like to proceed with the existing Selected or create a new one?
          </div>
          <div
            style={{
              alignItems: "flex-start",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Radio
              checked={modConfirmPerform?.type === "CREATE_NEW"}
              label="Create new"
              value="CREATE_NEW"
              onChange={(e, data) => {
                setModConfirmPerform({ ...modConfirmPerform, type: String(data.value) });
              }}
            />
            {treatmentPending.map((order) => (
              <Radio
                key={order.id}
                style={{ marginTop: "0.75rem" }}
                value="ADD_ITEM"
                onChange={(e, data) => {
                  setModConfirmPerform({
                    ...modConfirmPerform,
                    id: order.id,
                    type: String(data.value),
                  });
                }}
                checked={
                  modConfirmPerform?.type === "ADD_ITEM" && order.id === modConfirmPerform.id
                }
                label={order.items
                  .map((item) => {
                    const ordinal = getOrdinal(item.no);

                    return `With ${ordinal.number}${ordinal.suffix} ${item.plan_item_detail?.name}`;
                  })
                  .join(", ")}
              />
            ))}
          </div>
        </div>
      </ModInfo>

      <ModConfirm
        denyButtonStyle={{ marginLeft: "-1rem", width: "14rem" }}
        denyButtonText="Cancel the cancellation"
        modalStyle={{ width: "40rem" }}
        openModal={!!modConfirmCancel}
        titleColor="red"
        onCloseWithDimmerClick={handleCloseConfirmCancel}
        onDeny={handleCloseConfirmCancel}
        approveButton={
          <ButtonLoadCheck
            setProp={props.setProp}
            style={{ width: "14rem" }}
            color="green"
            paramKey={ACTION_CANCEL}
            size="medium"
            title="Confirm Cancellation"
            basic
            buttonLoadCheck={props.buttonLoadCheck?.[ACTION_CANCEL]}
            onClick={handleConfirmCancel}
          />
        }
        content={
          <Form style={{ margin: "-0.5rem 0 -0.75rem" }}>
            <Form.Field>
              {modConfirmCancel?.hasApp && (
                <>
                  <div style={{ fontWeight: "bold", marginBottom: "1rem", textAlign: "center" }}>
                    <div>This treatment plan has some scheduled appointments.</div>
                    <div>Do you want to cancel the appointments as well?</div>
                  </div>
                  <Radio
                    checked={modConfirmCancel.type === "WITHOUT_APPOINTMENT"}
                    label="Choose thisCancel only the items that are not scheduled"
                    name="WITHOUT_APPOINTMENT"
                    value={modConfirmCancel.type}
                    onChange={handleChangeType}
                  />
                  <Radio
                    style={{ marginTop: "0.5rem" }}
                    checked={modConfirmCancel.type === "WITH_APPOINTMENT"}
                    label="Cancel all remaining items along with the scheduled appointments"
                    name="WITH_APPOINTMENT"
                    value={modConfirmCancel.type}
                    onChange={handleChangeType}
                  />
                </>
              )}

              <div
                style={{
                  fontWeight: "bold",
                  marginBottom: "0.5rem",
                  ...(modConfirmCancel?.hasApp && { marginTop: "1rem", textAlign: "center" }),
                }}
              >
                Please specify the reason for canceling the remaining treatment plan
              </div>
              <TextArea value={modConfirmCancel?.note || ""} onChange={handleChangeNote} />
            </Form.Field>
          </Form>
        }
      />

      <ModConfirm
        backgroundColor="var(--primary-theme-color)"
        denyButtonStyle={{ marginLeft: "-1rem", width: "8.75rem" }}
        denyButtonText="CLOSE"
        openModal={!!modEditDepartment}
        titleName="Edit Order to department"
        onCloseWithDimmerClick={handleCloseModEditDepartment}
        onDeny={handleCloseModEditDepartment}
        approveButton={
          <ButtonLoadCheck
            setProp={props.setProp}
            style={{ width: "8.75rem" }}
            color="green"
            paramKey={ACTION_EDIT}
            size="medium"
            title="SAVE"
            basic
            buttonLoadCheck={props.buttonLoadCheck?.[ACTION_EDIT]}
            onClick={handleConfirmEdit}
          />
        }
        content={
          <Form style={{ margin: "-0.5rem 0 -1rem" }}>
            <Form.Field>
              <label style={{ fontSize: "0.95rem" }}>Please select new department</label>
              <Dropdown
                value={modEditDepartment?.to_department_for_physiotherapist ?? ""}
                search
                selection
                options={toDepartmentOptions}
                onChange={handleChangeToDepartment}
              />
            </Form.Field>
          </Form>
        }
      />

      <Modal
        style={{ width: "calc(100vw - 20rem)" }}
        open={openModSelectAppointment}
        closeOnDimmerClick
        onClose={handleCloseModSelectAppointment}
      >
        <CardSelectAppointmentDatetime
          onEvent={props.onEvent}
          setProp={props.setProp}
          proxyController={props.proxyController}
          checkedAppointmentList={checkedList}
          divisionId={props.divisionId}
          rehabDeviceSlot={props.rehabDeviceSlot}
          rehabOrderAppointmentList={props.rehabOrderAppointmentList}
          rehabTreatmentList={props.rehabTreatmentList}
          selectedRehabDeviceSlot={props.selectedRehabDeviceSlot}
          buttonLoadCheck={props.buttonLoadCheck}
          errorMessage={props.errorMessage}
          languageUX={props.languageUX}
          searchedItemListWithKey={props.searchedItemListWithKey}
          selectedPatient={props.selectedPatient}
          onClose={handleCloseModSelectAppointment}
          onSuccess={handleSuccess}
        />
      </Modal>
    </div>
  );
};

CardRehabTreatment.displayName = "CardRehabTreatment";

export default React.memo(CardRehabTreatment);
